import { PDFDocument } from 'pdf-lib';
import { Data, Effect, Schema } from 'effect';
import { FetchHttpClient, HttpBody, HttpClient, HttpClientRequest } from 'effect/unstable/http';

export const DEFAULT_UNSTRUCTURED_ENDPOINT = 'https://api.unstructuredapp.io/general/v0/general';
export const DEFAULT_UNSTRUCTURED_STRATEGY = 'vlm' as const;
export const DEFAULT_UNSTRUCTURED_BATCH_PAGES = 1;
export const DEFAULT_UNSTRUCTURED_CONCURRENCY = 4;
export const DEFAULT_UNSTRUCTURED_REQUEST_TIMEOUT_MS = 180_000;

const MAX_PROVIDER_ERROR_LENGTH = 400;

const UnstructuredElementRecordsSchema = Schema.Array(Schema.Record(Schema.String, Schema.Unknown));

export class UnstructuredParserError extends Data.TaggedError('UnstructuredParserError')<{
  readonly reason:
    | 'validation'
    | 'pdf'
    | 'transport'
    | 'status'
    | 'decode'
    | 'timeout'
    | 'callback';
  readonly message: string;
  readonly retryable: boolean;
  readonly status?: number;
  readonly cause?: unknown;
}> {}

export type UnstructuredStrategy = 'vlm' | 'hi_res' | 'fast' | 'ocr_only' | 'auto';

export interface UnstructuredElementMetadata {
  page_number?: number;
  [key: string]: unknown;
}

/** A lossless-enough shape for elements returned by Unstructured partition. */
export interface UnstructuredElement {
  type: string;
  element_id?: string;
  text?: string;
  metadata?: UnstructuredElementMetadata;
  [key: string]: unknown;
}

export interface UnstructuredBatchResult {
  /** Zero-based batch position in the source document. */
  batchIndex: number;
  /** Inclusive, one-based page number in the source document. */
  startPage: number;
  /** Inclusive, one-based page number in the source document. */
  endPage: number;
  pageCount: number;
  elements: UnstructuredElement[];
}

/** Short alias for the batch value delivered to onBatch. */
export type UnstructuredBatch = UnstructuredBatchResult;

export interface StreamUnstructuredPdfOptions {
  pdfBytes: Uint8Array | ArrayBuffer;
  apiKey: string;
  endpoint?: string;
  strategy?: UnstructuredStrategy;
  /** Number of source pages sent in each partition request. */
  batchPages?: number;
  /** Maximum simultaneous partition requests. */
  concurrency?: number;
  /** Timeout for each partition request in milliseconds. */
  requestTimeoutMs?: number;
  /** Process at most this many leading source pages. */
  maxPages?: number;
  fileName?: string;
  signal?: AbortSignal;
  fetchImpl?: typeof fetch;
  /** Called after the PDF is counted and before any partition request starts. */
  onPageCount?: (pageCount: number) => void | Promise<void>;
  /** Called when a batch completes. Concurrent batches may complete out of order. */
  onBatch?: (batch: UnstructuredBatchResult) => void | Promise<void>;
}

export type StreamUnstructuredPdfEffectOptions = Omit<
  StreamUnstructuredPdfOptions,
  'signal' | 'fetchImpl'
>;

export interface StreamUnstructuredPdfResult {
  /** Page count in the source PDF, before maxPages is applied. */
  totalPages: number;
  /** Number of leading pages processed after maxPages is applied. */
  pageCount: number;
  batchCount: number;
  /** Batches in source-page order, regardless of callback completion order. */
  batches: UnstructuredBatchResult[];
  /** Elements flattened in source-page batch order. */
  elements: UnstructuredElement[];
}

interface PdfBatch {
  batchIndex: number;
  startPage: number;
  endPage: number;
  pageCount: number;
  pdfBytes: ArrayBuffer;
}

function positiveInteger(value: number | undefined, fallback: number, name: string): number {
  const resolved = value ?? fallback;
  if (!Number.isSafeInteger(resolved) || resolved <= 0) {
    throw new TypeError(`${name} must be a positive integer`);
  }
  return resolved;
}

function optionalPositiveInteger(value: number | undefined, name: string): number | undefined {
  if (value === undefined) return undefined;
  return positiveInteger(value, value, name);
}

function arrayBufferFromBytes(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
}

function createAbortError(signal?: AbortSignal): Error {
  if (signal?.reason instanceof Error) return signal.reason;
  const error = new Error('The operation was aborted');
  error.name = 'AbortError';
  return error;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) throw createAbortError(signal);
}

function safeFileName(fileName: string | undefined, batch: PdfBatch): string {
  const fallback = `document-pages-${batch.startPage}-${batch.endPage}.pdf`;
  if (!fileName?.trim()) return fallback;
  const sanitized = fileName.trim().replace(/[\u0000-\u001f\u007f/\\]/g, '_');
  return sanitized.toLowerCase().endsWith('.pdf') ? sanitized : `${sanitized}.pdf`;
}

function redactAndTruncateProviderText(text: string, apiKey: string): string {
  let safe = text;
  if (apiKey) safe = safe.split(apiKey).join('[REDACTED]');
  safe = safe
    .replace(/\bBearer\s+[^\s,;]+/gi, 'Bearer [REDACTED]')
    .replace(
      /(unstructured-api-key|api[-_ ]?key)(\s*["']?\s*[:=]\s*["']?)[^\s,"'};]+/gi,
      '$1$2[REDACTED]',
    )
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (safe.length <= MAX_PROVIDER_ERROR_LENGTH) return safe;
  return `${safe.slice(0, MAX_PROVIDER_ERROR_LENGTH - 3)}...`;
}

function providerError(status: number, body: string, apiKey: string): UnstructuredParserError {
  const detail = redactAndTruncateProviderText(body, apiKey);
  return new UnstructuredParserError({
    reason: 'status',
    status,
    message: `Unstructured API error ${status}${detail ? `: ${detail}` : ''}`,
    retryable: status === 408 || status === 429 || status >= 500,
  });
}

function normalizeBatchPageNumbers(
  elements: UnstructuredElement[],
  startPage: number,
  endPage: number,
): UnstructuredElement[] {
  if (startPage <= 1 || elements.length === 0) return elements;

  const pageNumbers = elements
    .map((element) => element.metadata?.page_number)
    .filter((pageNumber): pageNumber is number => Number.isSafeInteger(pageNumber));
  if (pageNumbers.length === 0) return elements;

  const batchPageCount = endPage - startPage + 1;
  const allLocal = pageNumbers.every(
    (pageNumber) => pageNumber >= 1 && pageNumber <= batchPageCount,
  );
  const allGlobal = pageNumbers.every(
    (pageNumber) => pageNumber >= startPage && pageNumber <= endPage,
  );
  if (!allLocal || allGlobal) return elements;

  return elements.map((element) => {
    const pageNumber = element.metadata?.page_number;
    if (!Number.isSafeInteger(pageNumber)) return element;
    return {
      ...element,
      metadata: {
        ...element.metadata,
        page_number: (pageNumber as number) + startPage - 1,
      },
    };
  });
}

function validateElements(payload: unknown): UnstructuredElement[] {
  if (!Array.isArray(payload)) {
    throw new Error('Unstructured API returned an invalid response: expected an element array');
  }
  for (const element of payload) {
    if (!element || typeof element !== 'object' || typeof element.type !== 'string') {
      throw new Error('Unstructured API returned an invalid element');
    }
  }
  return [...payload] as UnstructuredElement[];
}

async function splitPdf(
  source: PDFDocument,
  pageCount: number,
  batchPages: number,
  signal?: AbortSignal,
): Promise<PdfBatch[]> {
  const batches: PdfBatch[] = [];
  let batchIndex = 0;
  for (let startIndex = 0; startIndex < pageCount; startIndex += batchPages) {
    throwIfAborted(signal);
    const endIndex = Math.min(startIndex + batchPages, pageCount);
    const output = await PDFDocument.create();
    const pageIndices = Array.from(
      { length: endIndex - startIndex },
      (_, offset) => startIndex + offset,
    );
    const copiedPages = await output.copyPages(source, pageIndices);
    for (const page of copiedPages) output.addPage(page);
    const bytes = await output.save();
    batches.push({
      batchIndex,
      startPage: startIndex + 1,
      endPage: endIndex,
      pageCount: endIndex - startIndex,
      pdfBytes: arrayBufferFromBytes(bytes),
    });
    batchIndex += 1;
  }
  return batches;
}

function partitionBatchEffect(options: {
  batch: PdfBatch;
  apiKey: string;
  endpoint: string;
  strategy: UnstructuredStrategy;
  fileName?: string;
  requestTimeoutMs: number;
}): Effect.Effect<UnstructuredBatchResult, UnstructuredParserError, HttpClient.HttpClient> {
  const { batch, apiKey, endpoint, strategy, requestTimeoutMs } = options;
  return Effect.gen(function* () {
    const form = yield* Effect.try({
      try: () => {
        const value = new FormData();
        value.append(
          'files',
          new Blob([batch.pdfBytes], { type: 'application/pdf' }),
          safeFileName(options.fileName, batch),
        );
        value.append('strategy', strategy);
        value.append('coordinates', 'true');
        value.append('output_format', 'application/json');
        value.append('starting_page_number', String(batch.startPage));
        return value;
      },
      catch: () =>
        new UnstructuredParserError({
          reason: 'pdf',
          message: 'Unable to create the Unstructured multipart request',
          retryable: false,
        }),
    });
    const request = HttpClientRequest.post(endpoint).pipe(
      HttpClientRequest.acceptJson,
      HttpClientRequest.setHeader('unstructured-api-key', apiKey),
      HttpClientRequest.setBody(HttpBody.formData(form)),
    );
    const response = yield* HttpClient.execute(request).pipe(
      Effect.provideService(FetchHttpClient.RequestInit, { redirect: 'error' }),
      Effect.mapError(
        () =>
          new UnstructuredParserError({
            reason: 'transport',
            message: 'Unstructured API request failed',
            retryable: true,
          }),
      ),
    );

    if (response.status < 200 || response.status >= 300) {
      const body = yield* response.text.pipe(Effect.orElseSucceed(() => ''));
      return yield* providerError(response.status, body, apiKey);
    }

    const payload = yield* response.json.pipe(
      Effect.mapError(
        () =>
          new UnstructuredParserError({
            reason: 'decode',
            message: 'Unstructured API returned invalid JSON',
            retryable: false,
          }),
      ),
    );
    const records = yield* Schema.decodeUnknownEffect(UnstructuredElementRecordsSchema)(
      payload,
    ).pipe(
      Effect.mapError(
        () =>
          new UnstructuredParserError({
            reason: 'decode',
            message: 'Unstructured API returned an invalid response: expected an element array',
            retryable: false,
          }),
      ),
    );
    const elements = yield* Effect.try({
      try: () => validateElements(records),
      catch: (cause) =>
        new UnstructuredParserError({
          reason: 'decode',
          message:
            cause instanceof Error ? cause.message : 'Unstructured API returned invalid elements',
          retryable: false,
        }),
    });
    return {
      batchIndex: batch.batchIndex,
      startPage: batch.startPage,
      endPage: batch.endPage,
      pageCount: batch.pageCount,
      elements: normalizeBatchPageNumbers(elements, batch.startPage, batch.endPage),
    };
  }).pipe(
    Effect.timeout(requestTimeoutMs),
    Effect.catchTag('TimeoutError', () =>
      Effect.fail(
        new UnstructuredParserError({
          reason: 'timeout',
          message: `Unstructured API request timed out after ${requestTimeoutMs}ms`,
          retryable: true,
        }),
      ),
    ),
  );
}

interface ResolvedStreamOptions {
  readonly pdfBytes: Uint8Array | ArrayBuffer;
  readonly apiKey: string;
  readonly endpoint: string;
  readonly strategy: UnstructuredStrategy;
  readonly batchPages: number;
  readonly concurrency: number;
  readonly requestTimeoutMs: number;
  readonly maxPages?: number;
  readonly fileName?: string;
  readonly onPageCount?: StreamUnstructuredPdfEffectOptions['onPageCount'];
  readonly onBatch?: StreamUnstructuredPdfEffectOptions['onBatch'];
}

function resolveStreamOptions(options: StreamUnstructuredPdfEffectOptions): ResolvedStreamOptions {
  const apiKey = options.apiKey.trim();
  if (!apiKey) throw new TypeError('apiKey is required');
  return {
    pdfBytes: options.pdfBytes,
    apiKey,
    endpoint: options.endpoint?.trim() || DEFAULT_UNSTRUCTURED_ENDPOINT,
    strategy: options.strategy ?? DEFAULT_UNSTRUCTURED_STRATEGY,
    batchPages: positiveInteger(options.batchPages, DEFAULT_UNSTRUCTURED_BATCH_PAGES, 'batchPages'),
    concurrency: positiveInteger(
      options.concurrency,
      DEFAULT_UNSTRUCTURED_CONCURRENCY,
      'concurrency',
    ),
    maxPages: optionalPositiveInteger(options.maxPages, 'maxPages'),
    requestTimeoutMs: positiveInteger(
      options.requestTimeoutMs,
      DEFAULT_UNSTRUCTURED_REQUEST_TIMEOUT_MS,
      'requestTimeoutMs',
    ),
    fileName: options.fileName,
    onPageCount: options.onPageCount,
    onBatch: options.onBatch,
  };
}

function callbackEffect(
  label: string,
  callback: (() => void | Promise<void>) | undefined,
): Effect.Effect<void, UnstructuredParserError> {
  if (!callback) return Effect.succeed(undefined);
  return Effect.tryPromise({
    try: async () => callback(),
    catch: (cause) =>
      new UnstructuredParserError({
        reason: 'callback',
        message: `${label} callback failed`,
        retryable: false,
        cause,
      }),
  });
}

/**
 * Split a PDF into ordered page batches, partition them concurrently through
 * Unstructured, and expose each completed batch without accepting partial success.
 */
export function streamUnstructuredPdfEffect(
  options: StreamUnstructuredPdfEffectOptions,
): Effect.Effect<StreamUnstructuredPdfResult, UnstructuredParserError, HttpClient.HttpClient> {
  return Effect.gen(function* () {
    const resolved = yield* Effect.try({
      try: () => resolveStreamOptions(options),
      catch: (cause) =>
        new UnstructuredParserError({
          reason: 'validation',
          message: cause instanceof Error ? cause.message : String(cause),
          retryable: false,
          cause,
        }),
    });
    const source = yield* Effect.tryPromise({
      try: (signal) => {
        throwIfAborted(signal);
        return PDFDocument.load(resolved.pdfBytes, { ignoreEncryption: true });
      },
      catch: (cause) =>
        new UnstructuredParserError({
          reason: 'pdf',
          message: cause instanceof Error ? cause.message : 'Unable to load PDF',
          retryable: false,
          cause,
        }),
    });
    const totalPages = source.getPageCount();
    const pageCount = Math.min(totalPages, resolved.maxPages ?? totalPages);

    yield* callbackEffect(
      'onPageCount',
      resolved.onPageCount ? () => resolved.onPageCount!(pageCount) : undefined,
    );

    const pdfBatches = yield* Effect.tryPromise({
      try: (signal) => splitPdf(source, pageCount, resolved.batchPages, signal),
      catch: (cause) =>
        new UnstructuredParserError({
          reason: 'pdf',
          message: cause instanceof Error ? cause.message : 'Unable to split PDF',
          retryable: false,
          cause,
        }),
    });
    const batches = yield* Effect.forEach(
      pdfBatches,
      (batch) =>
        partitionBatchEffect({
          batch,
          apiKey: resolved.apiKey,
          endpoint: resolved.endpoint,
          strategy: resolved.strategy,
          fileName: resolved.fileName,
          requestTimeoutMs: resolved.requestTimeoutMs,
        }).pipe(
          Effect.tap((result) =>
            callbackEffect(
              'onBatch',
              resolved.onBatch ? () => resolved.onBatch!(result) : undefined,
            ),
          ),
        ),
      { concurrency: resolved.concurrency },
    );
    const orderedBatches = [...batches];
    return {
      totalPages,
      pageCount,
      batchCount: orderedBatches.length,
      batches: orderedBatches,
      elements: orderedBatches.flatMap((batch) => batch.elements),
    };
  });
}

function toPromiseError(error: UnstructuredParserError): Error {
  if (
    (error.reason === 'validation' || error.reason === 'pdf' || error.reason === 'callback') &&
    error.cause instanceof Error
  ) {
    return error.cause;
  }
  const legacy = new Error(error.message) as Error & { status?: number };
  if (error.reason === 'timeout') legacy.name = 'TimeoutError';
  if (error.status !== undefined) legacy.status = error.status;
  return legacy;
}

export function streamUnstructuredPdf(
  options: StreamUnstructuredPdfOptions,
): Promise<StreamUnstructuredPdfResult> {
  const { fetchImpl = fetch.bind(globalThis), signal, ...effectOptions } = options;
  const program = streamUnstructuredPdfEffect(effectOptions).pipe(
    Effect.provideService(FetchHttpClient.Fetch, fetchImpl),
    Effect.provideService(HttpClient.TracerPropagationEnabled, false),
    Effect.provide(FetchHttpClient.layer),
  );
  return Effect.runPromise(program, { signal }).catch((error: unknown) => {
    if (signal?.aborted) throw createAbortError(signal);
    if (error instanceof UnstructuredParserError) throw toPromiseError(error);
    throw error;
  });
}
